/**
 * Server-side structured data for the LoanOfficerQuestions tool page.
 * Lives in a data module (not the page component) so routeMeta can emit it
 * into the server HTML; react-helmet would only add it after hydration.
 */
export const FAQS = [
  {
    id: "apr_vs_rate",
    q: "What's the difference between the interest rate and the APR?",
    a: "The interest rate determines your monthly principal and interest payment. The APR adds most lender fees into a single annualized number, which makes it easier to compare two quotes fairly. APR assumes you keep the loan for its full term, so if you expect to sell or refinance in a few years, compare the five-year cost and the break-even on any upfront points instead.",
  },
  {
    id: "rate_lock",
    q: "How long should my rate lock be?",
    a: "Match the lock to a realistic closing timeline rather than the shortest option that produces the best rate. Thirty to forty-five days is common for a purchase. Ask what an extension costs per day in writing, because a lock that expires before closing can leave you repriced at current market rates.",
  },
  {
    id: "pmi",
    q: "When does mortgage insurance come off?",
    a: "On a conventional loan, private mortgage insurance can generally be removed once you reach a set equity threshold, and lenders are required to terminate it automatically at a later threshold based on the original amortization schedule. FHA mortgage insurance often lasts the life of the loan depending on when it was originated and how much you put down. Ask the loan officer which rule applies to your specific program.",
  },
  {
    id: "prepayment",
    q: "Do mortgages still have prepayment penalties?",
    a: "They are far less common on standard owner-occupied mortgages than they once were, and many loan types restrict or prohibit them. They can still appear on some non-qualified or investor loans. Ask the question directly and confirm the answer on your Loan Estimate and closing documents rather than relying on a verbal reply.",
  },
  {
    id: "underwriting",
    q: "What does underwriting actually check?",
    a: "An underwriter verifies income, employment, assets, credit, the property appraisal, and how everything fits the program's guidelines. Conditional approval means they still want documents or explanations. Clear-to-close means the conditions are satisfied. Ask for your condition list in writing so nothing surfaces late.",
  },
  {
    id: "timeline",
    q: "How long does closing take from application?",
    a: "Ask your loan officer for a range rather than a date, and ask what typically causes slippage on their files. Appraisal scheduling, self-employment income documentation, gift fund paperwork, and title issues are the usual causes. Build the answer into both your contract dates and your rate lock length.",
  },
];

  export const LOAN_OFFICER_QUESTIONS_JSON_LD: Record<string, unknown> = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "WebPage",
      name: "Questions to Ask a Loan Officer Before You Commit",
      url: "https://www.homeapproach.com/tools/loan-officer-questions",
      description:
        "A 12-question checklist for lender calls, covering loan options, costs and fees, rate lock, underwriting, and communication \u2014 with the red flags to watch for.",
      isPartOf: { "@type": "WebSite", name: "Home Approach", url: "https://www.homeapproach.com" },
      publisher: { "@type": "Organization", name: "Home Approach", url: "https://www.homeapproach.com" },
    },
    {
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: "https://www.homeapproach.com/" },
        { "@type": "ListItem", position: 2, name: "Tools", item: "https://www.homeapproach.com/tools" },
        {
          "@type": "ListItem",
          position: 3,
          name: "Loan Officer Questions",
          item: "https://www.homeapproach.com/tools/loan-officer-questions",
        },
      ],
    },
    {
      "@type": "FAQPage",
      mainEntity: FAQS.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    },
  ],
};
